import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { negotiationsAPI } from '../../api/negotiationsAPI';

export const startNegotiation = createAsyncThunk(
  'negotiations/startNegotiation',
  async ({ itemId, sellerId, listingType = 'item' }, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.startNegotiation(itemId, sellerId, listingType);
      return response;
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.error) {
        return rejectWithValue(errorData.error);
      }
      if (errorData?.details) {
        return rejectWithValue(JSON.stringify(errorData.details));
      }
      return rejectWithValue(errorData || error.message || 'Failed to start negotiation');
    }
  }
);

export const fetchActiveNegotiations = createAsyncThunk(
  'negotiations/fetchActiveNegotiations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.getActiveNegotiations();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch negotiations');
    }
  }
);

export const fetchNegotiationDetail = createAsyncThunk(
  'negotiations/fetchNegotiationDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.getNegotiationDetail(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch negotiation detail');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'negotiations/fetchMessages',
  async (id, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.getMessages(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'negotiations/sendMessage',
  async ({ id, content }, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.sendMessage(id, content);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to send message');
    }
  }
);

export const makeOffer = createAsyncThunk(
  'negotiations/makeOffer',
  async ({ id, pointsAmount }, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.makeOffer(id, pointsAmount);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to make offer');
    }
  }
);

export const acceptOffer = createAsyncThunk(
  'negotiations/acceptOffer',
  async (id, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.acceptOffer(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to accept offer');
    }
  }
);

export const rejectOffer = createAsyncThunk(
  'negotiations/rejectOffer',
  async (id, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.rejectOffer(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to reject offer');
    }
  }
);

export const fetchUnreadNotificationsCount = createAsyncThunk(
  'negotiations/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const count = await negotiationsAPI.getUnreadNotificationsCount();
      return count;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch unread count');
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  'negotiations/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await negotiationsAPI.getNotifications();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'negotiations/markNotificationRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      await negotiationsAPI.markNotificationRead(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notification as read');
    }
  }
);

export const markSeenOutcomes = createAsyncThunk(
  'negotiations/markSeenOutcomes',
  async (_, { rejectWithValue }) => {
    try {
      return await negotiationsAPI.markSeenOutcomes();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark seen outcomes');
    }
  }
);

const negotiationsSlice = createSlice({
  name: 'negotiations',
  initialState: {
    activeNegotiations: [],
    currentNegotiation: null,
    messages: [],
    notifications: [],
    loading: false,
    error: null,
    unreadCount: 0,
  },
  reducers: {
    clearCurrentNegotiation: (state) => {
      state.currentNegotiation = null;
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    // Start negotiation
    builder
      .addCase(startNegotiation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startNegotiation.fulfilled, (state, action) => {
        state.loading = false;
        state.activeNegotiations.push(action.payload);
      })
      .addCase(startNegotiation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch active negotiations
    builder
      .addCase(fetchActiveNegotiations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveNegotiations.fulfilled, (state, action) => {
        state.loading = false;
        state.activeNegotiations = action.payload;
      })
      .addCase(fetchActiveNegotiations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch negotiation detail
    builder
      .addCase(fetchNegotiationDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNegotiationDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentNegotiation = action.payload;
      })
      .addCase(fetchNegotiationDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages.push(action.payload);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Make offer
    builder
      .addCase(makeOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(makeOffer.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentNegotiation) {
          if (!state.currentNegotiation.offers) {
            state.currentNegotiation.offers = [];
          }
          state.currentNegotiation.offers.push(action.payload);
        }
      })
      .addCase(makeOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Accept offer
    builder
      .addCase(acceptOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptOffer.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentNegotiation) {
          state.currentNegotiation.status = 'COMPLETED';
          const offerIndex = state.currentNegotiation.offers?.findIndex(
            (o) => o.id === action.payload.id
          );
          if (offerIndex !== -1) {
            state.currentNegotiation.offers[offerIndex] = action.payload;
          }
        }
      })
      .addCase(acceptOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Reject offer
    builder
      .addCase(rejectOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectOffer.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentNegotiation) {
          const offerIndex = state.currentNegotiation.offers?.findIndex(
            (o) => o.id === action.payload.id
          );
          if (offerIndex !== -1) {
            state.currentNegotiation.offers[offerIndex] = action.payload;
          }
        }
      })
      .addCase(rejectOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch unread count
    builder
      .addCase(fetchUnreadNotificationsCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadNotificationsCount.rejected, (state) => {
        // Don't set error for unread count - it's not critical
        state.unreadCount = 0;
      });

    // Fetch notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
        state.unreadCount = action.payload.filter((n) => !n.is_read).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Mark notification read
    builder
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.notifications.find((n) => n.id === notificationId);
        const negotiationId = notification?.negotiation?.id ?? notification?.negotiation;
        if (negotiationId != null) {
          state.notifications.forEach((n) => {
            const nNegId = n.negotiation?.id ?? n.negotiation;
            if (nNegId === negotiationId) {
              n.is_read = true;
            }
          });
        } else if (notification) {
          notification.is_read = true;
        }
        state.unreadCount = state.notifications.filter((n) => !n.is_read).length;
      });

    builder
      .addCase(markSeenOutcomes.fulfilled, (state, action) => {
        const completedIds = new Set(action.payload?.negotiation_ids || []);
        const outcomeTypes = new Set(['OFFER_ACCEPTED', 'OFFER_REJECTED']);
        state.notifications.forEach((n) => {
          const nNegId = n.negotiation?.id ?? n.negotiation;
          if (completedIds.has(nNegId) || outcomeTypes.has(n.notification_type)) {
            n.is_read = true;
          }
        });
        if (typeof action.payload?.unread_count === 'number') {
          state.unreadCount = action.payload.unread_count;
        } else {
          state.unreadCount = state.notifications.filter((n) => !n.is_read).length;
        }
      });
  },
});

export const { clearCurrentNegotiation } = negotiationsSlice.actions;
export default negotiationsSlice.reducer;
